import { z } from 'zod';

/** Improvement Kit action types (kit_outputs.action_type + findings.kit_action). */
export const IMPROVEMENT_ACTIONS = [
  'service_page',
  'trust_section',
  'contact_section',
  'technical_seo',
  'seo_plan',
] as const;

export type ImprovementAction = (typeof IMPROVEMENT_ACTIONS)[number];

/** Map review-engine kitAction values onto kit home cards. */
export const KIT_ACTION_ALIASES: Record<string, ImprovementAction> = {
  service_page: 'service_page',
  trust_section: 'trust_section',
  contact_section: 'contact_section',
  checklist: 'seo_plan',
  technical_seo: 'technical_seo',
  seo_plan: 'seo_plan',
};

export function resolveImprovementAction(raw: string | null | undefined): ImprovementAction | null {
  if (!raw) return null;
  return KIT_ACTION_ALIASES[raw] ?? null;
}

export const TRADE_PACK_IDS = [
  'plumbing',
  'hvac',
  'electrical',
  'roofing',
  'landscaping',
  'painting',
  'remodeling',
  'general',
] as const;

export type TradePackId = (typeof TRADE_PACK_IDS)[number];

export const businessFactsSchema = z.object({
  businessName: z.string().default(''),
  trade: z.string().default(''),
  tradePackId: z.enum(TRADE_PACK_IDS).default('general'),
  phone: z.string().default(''),
  publicEmail: z.string().default(''),
  websiteUrl: z.string().default(''),
  address: z.string().default(''),
  city: z.string().default(''),
  state: z.string().default(''),
  services: z.array(z.string()).default([]),
  serviceArea: z.array(z.string()).default([]),
  differentiators: z.array(z.string()).default([]),
  licenseNumber: z.string().default(''),
  yearsInBusiness: z.number().int().positive().nullable().default(null),
  warranty: z.string().default(''),
  hours: z.string().default(''),
  insured: z.boolean().default(false),
  bonded: z.boolean().default(false),
  certifications: z.array(z.string()).default([]),
  associations: z.array(z.string()).default([]),
  reviewLinks: z.array(z.string()).default([]),
  responsePromise: z.string().default(''),
  emergencyAvailability: z.string().default(''),
  preferredContact: z.enum(['call', 'text', 'form']).default('call'),
  canReceiveSms: z.boolean().default(false),
  selectedService: z.string().default(''),
  primaryCta: z.enum(['phone', 'quote', 'contact']).default('phone'),
  quotePageUrl: z.string().default(''),
  processSteps: z.array(z.string()).default([]),
  commonQuestions: z.array(z.object({ q: z.string(), a: z.string() })).default([]),
  trustLayout: z.enum(['badges', 'grid', 'proof_strip']).default('badges'),
  existingServicePageUrl: z.string().default(''),
});

export type BusinessFacts = z.infer<typeof businessFactsSchema>;

export function emptyFacts(partial?: Partial<BusinessFacts>): BusinessFacts {
  return businessFactsSchema.parse(partial ?? {});
}

export function parseFacts(raw: unknown): BusinessFacts {
  if (typeof raw === 'string') {
    try {
      return businessFactsSchema.parse(JSON.parse(raw));
    } catch {
      return emptyFacts();
    }
  }
  return businessFactsSchema.parse(raw ?? {});
}

/** Whether a string fact is present (trimmed non-empty). */
export function hasFact(value: string | null | undefined): boolean {
  return Boolean(value && value.trim().length > 0);
}

export function hasListFact(value: string[] | null | undefined): boolean {
  return Boolean(value && value.some((v) => v.trim().length > 0));
}

export const OUTPUT_STATUSES = ['not_started', 'draft', 'copied', 'done'] as const;
export type OutputStatus = (typeof OUTPUT_STATUSES)[number];

export const PLAN_TASK_STATUSES = ['not_started', 'in_progress', 'implemented', 'dismissed'] as const;
export type PlanTaskStatus = (typeof PLAN_TASK_STATUSES)[number];

export type EffortBand = 'small' | 'medium' | 'substantial';

export type SeoPlanTask = {
  id: string;
  name: string;
  priority: number;
  findingIds: string[];
  checkId?: string;
  effortBand: EffortBand;
  category: string;
  instructions: string;
  status: PlanTaskStatus;
  group: 'this_week' | 'next' | 'later';
  kitAction?: ImprovementAction | null;
};

export type GeneratedDraft = {
  title: string;
  plainText: string;
  html: string;
  fields: Record<string, string>;
  jsonLd?: string | null;
  placeholders: string[];
  warnings: string[];
  lintIssues: string[];
};

export const WEBSITE_PLATFORMS = [
  'wordpress',
  'wix',
  'squarespace',
  'godaddy',
  'webflow',
  'shopify',
  'other',
] as const;

export type WebsitePlatform = (typeof WEBSITE_PLATFORMS)[number];
